import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useScrollSync } from '../contexts/ScrollSyncContext';
import { createViewportLinkedScrollCommandChannel } from '../components/viewportLinkedScrollCommandChannel';

/**
 * useLinkedScroll - manages synchronized scrolling across multiple viewports
 *
 * Returns:
 *   linkedScroll: boolean - whether linked scrolling is enabled
 *   toggleLinkedScroll: function
 *   onSliceChange: function(viewportIdx, newSlice, totalSlices) - call when a viewport scrolls
 *   linkedScrollFraction: function(targetViewportIdx) => number | null
 *     Returns fraction to jump to (0-1) for the given viewport, or null to stay at current
 */
export function useLinkedScroll(viewportIdx) {
  const [linkedScroll, setLinkedScroll] = useState(false);
  const [latestLinkedScroll, setLatestLinkedScroll] = useState(null);
  const { registerBroadcast, broadcast } = useScrollSync();
  const latestRequestedLinkedRef = useRef(null);
  const linkedScrollMountedRef = useRef(true);
  const linkedAuthorityEpochRef = useRef(0);
  const linkedScrollEnabledRef = useRef(linkedScroll);
  linkedScrollEnabledRef.current = linkedScroll;
  const linkedCommandSequenceRef = useRef(0);
  const linkedScrollCommandChannel = useMemo(() => createViewportLinkedScrollCommandChannel(), []);

  const resetLinkedScrollPositions = useCallback(() => {
    if (!linkedScrollMountedRef.current) return;
    linkedAuthorityEpochRef.current += 1;
    linkedScrollCommandChannel.publish(null);
    setLatestLinkedScroll(null);
    latestRequestedLinkedRef.current = null;
  }, [linkedScrollCommandChannel]);

  const toggleLinkedScroll = useCallback((seed = null) => {
    if (!linkedScrollMountedRef.current) return;
    const next = !linkedScrollEnabledRef.current;
    linkedScrollEnabledRef.current = next;
    // Neither transition may inherit an old authority. In particular, enabling
    // LINK after local unlinked navigation must not replay the prior fraction.
    resetLinkedScrollPositions();
    if (
      next
      && seed
      && Number.isFinite(seed.sourceViewportIdx)
      && Number.isFinite(seed.newSlice)
      && Number.isFinite(seed.totalSlices)
      && seed.totalSlices > 0
    ) {
      const fraction = seed.totalSlices > 1 ? seed.newSlice / (seed.totalSlices - 1) : 0;
      const commandId = ++linkedCommandSequenceRef.current;
      const payload = {
        slice: seed.newSlice,
        total: seed.totalSlices,
        fraction,
        sourceViewportIdx: seed.sourceViewportIdx,
        commandId,
        linkedAuthorityEpoch: linkedAuthorityEpochRef.current,
        phase: 'accepted',
      };
      latestRequestedLinkedRef.current = {
        srcViewportIdx: seed.sourceViewportIdx,
        slice: seed.newSlice,
        total: seed.totalSlices,
      };
      setLatestLinkedScroll(payload);
      linkedScrollCommandChannel.publish(payload);
    }
    setLinkedScroll(next);
  }, [linkedScrollCommandChannel, resetLinkedScrollPositions]);

  // Register broadcast callback; clean up on unmount or viewportIdx change
  useEffect(() => {
    const handler = (sourceViewportIdx, fraction) => {
      if (!linkedScrollEnabledRef.current) return;
      // The page-level aggregate hook has no viewportIdx and already records its
      // own authoritative command synchronously in onSliceChange. Processing its
      // queued provider broadcast would manufacture a second command identity.
      if (viewportIdx == null) return;
      // Explicitly indexed hooks ignore their own broadcasts.
      if (sourceViewportIdx === viewportIdx) return;
      const payload = { sourceViewportIdx, fraction, commandId: ++linkedCommandSequenceRef.current };
      setLatestLinkedScroll(payload);
    };
    const cleanup = registerBroadcast(handler);
    return cleanup; // deregister on unmount
  }, [viewportIdx, registerBroadcast]);

  const onSliceChange = useCallback((srcViewportIdx, newSlice, totalSlices) => {
    if (!linkedScrollMountedRef.current || !linkedScrollEnabledRef.current) return null;

    // Calculate fractional position (0–1) of this slice. This remains the fallback
    // path; geometry-first triangulation commands bypass this fraction mapping.
    const fraction = totalSlices > 1 ? newSlice / (totalSlices - 1) : 0;

    // Requested authority is a synchronous, transport-free clinical fence. Do
    // not wait for an animation frame: decoded authority from the prior request
    // must become stale before this input handler returns. Followers abort LINK
    // work but do not start transport until the matching accepted phase.
    latestRequestedLinkedRef.current = { srcViewportIdx, slice: newSlice, total: totalSlices };
    const commandId = ++linkedCommandSequenceRef.current;
    linkedScrollCommandChannel.publish({
      sourceViewportIdx: srcViewportIdx,
      slice: newSlice,
      total: totalSlices,
      fraction,
      commandId,
      linkedAuthorityEpoch: linkedAuthorityEpochRef.current,
      phase: 'intent',
    });
    // The only live hook is page-aggregate (viewportIdx == null), whose registered
    // handler is an explicit no-op. Preserve the legacy signal without React state.
    broadcast(srcViewportIdx, fraction);
    return commandId;
  }, [broadcast, linkedScrollCommandChannel]);

  const onAcceptedSliceChange = useCallback((srcViewportIdx, newSlice, totalSlices, options = {}) => {
    if (!linkedScrollMountedRef.current || !linkedScrollEnabledRef.current) return null;
    const fraction = totalSlices > 1 ? newSlice / (totalSlices - 1) : 0;
    const latestRequested = latestRequestedLinkedRef.current;
    // A decoded source frame cannot publish transport authority behind a newer
    // requested target. Match the exact source command identity, not a floating
    // fraction that can alias across different slice/stack pairs.
    if (
      options.force !== true
      && latestRequested
      && (
        latestRequested.srcViewportIdx !== srcViewportIdx
        || latestRequested.slice !== newSlice
        || latestRequested.total !== totalSlices
      )
    ) return null;
    const currentPayload = linkedScrollCommandChannel.getSnapshot?.()?.payload;
    if (
      currentPayload?.phase === 'accepted'
      && currentPayload.sourceViewportIdx === srcViewportIdx
      && currentPayload.slice === newSlice
      && currentPayload.total === totalSlices
      && currentPayload.linkedAuthorityEpoch === linkedAuthorityEpochRef.current
    ) return currentPayload.commandId;
    const matchingIntent = currentPayload?.phase === 'intent'
      && currentPayload.sourceViewportIdx === srcViewportIdx
      && currentPayload.slice === newSlice
      && currentPayload.total === totalSlices
      && currentPayload.linkedAuthorityEpoch === linkedAuthorityEpochRef.current
      && Number.isSafeInteger(currentPayload.commandId) && currentPayload.commandId > 0;
    latestRequestedLinkedRef.current = { srcViewportIdx, slice: newSlice, total: totalSlices };
    const commandId = matchingIntent ? currentPayload.commandId : ++linkedCommandSequenceRef.current;
    linkedScrollCommandChannel.publish({
      sourceViewportIdx: srcViewportIdx,
      slice: newSlice,
      total: totalSlices,
      fraction,
      commandId,
      linkedAuthorityEpoch: linkedAuthorityEpochRef.current,
      phase: 'accepted',
    });
    return commandId;
  }, [linkedScrollCommandChannel]);

  useEffect(() => {
    linkedScrollMountedRef.current = true;
    return () => {
      linkedScrollMountedRef.current = false;
      linkedAuthorityEpochRef.current += 1;
      latestRequestedLinkedRef.current = null;
      linkedScrollCommandChannel.publish(null);
    };
  }, [linkedScrollCommandChannel]);

  /**
   * Returns the target fraction for a given viewport index, or null if nothing to sync.
   * Callers pass their own index so we exclude their own scroll from the result.
   */
  const linkedScrollFraction = useCallback((targetViewportIdx) => {
    if (!linkedScroll || !latestLinkedScroll) return null;
    if (latestLinkedScroll.sourceViewportIdx === targetViewportIdx) return null;
    return latestLinkedScroll.fraction;
  }, [latestLinkedScroll, linkedScroll]);

  const linkedScrollCommandId = useCallback((targetViewportIdx) => {
    if (!linkedScroll || !latestLinkedScroll) return null;
    if (latestLinkedScroll.sourceViewportIdx === targetViewportIdx) return null;
    return latestLinkedScroll.commandId;
  }, [latestLinkedScroll, linkedScroll]);

  return {
    linkedScroll,
    linkedAuthorityEpoch: linkedAuthorityEpochRef.current,
    toggleLinkedScroll,
    onSliceChange,
    onAcceptedSliceChange,
    linkedScrollFraction,
    linkedScrollCommandId,
    linkedScrollCommandChannel,
    resetLinkedScrollPositions,
  };
}
